import dayjs from 'dayjs';
import type { VacancyApiResponse } from '../services/types/vacancy.types';

export type VacancyLocationRow = VacancyApiResponse['locations'][number];

/** Days until due date from "now" (ceil); 0 if past or invalid. Used in view vacancy summary. */
export function calculateDaysRemaining(
  vacancyDueDate: string | null | undefined,
  now: Date = new Date(),
): number {
  if (!vacancyDueDate) return 0;
  const dueDate = new Date(vacancyDueDate);
  const diffTime = dueDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}

/** Locale string for vacancy view amounts (thousand separators, up to 2 decimals). */
export function formatVacancyViewNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * HTML snippet for budget line in vacancy summary (1 / 2 / 3+ locations).
 */
export function buildBudgetText(locationsWithBudgets: VacancyLocationRow[]): string {
  if (locationsWithBudgets.length === 0) {
    return '';
  }

  const budgetDetails = locationsWithBudgets.map((location) => {
    const amount = location.budgetAmount !== null ? location.budgetAmount : 0;
    const currency =
      location.budgetCurrency && location.budgetCurrency.trim() !== ''
        ? location.budgetCurrency
        : 'USD';
    const countryName = location.country?.name || '';
    return {
      amount: formatVacancyViewNumber(amount),
      currency,
      countryName,
    };
  });

  if (budgetDetails.length === 1) {
    const budget = budgetDetails[0];
    return `with a budget of <b>${budget.amount} ${budget.currency}</b> per resource`;
  }
  if (budgetDetails.length === 2) {
    const budgetStrings = budgetDetails.map(
      (budget) =>
        `<b>${budget.amount} ${budget.currency}</b> per resource for <b>${budget.countryName}</b>`,
    );
    return `with a budget of ${budgetStrings[0]}, and ${budgetStrings[1]}`;
  }
  const budgetStrings = budgetDetails.map(
    (budget) =>
      `<b>${budget.amount} ${budget.currency}</b> per resource for <b>${budget.countryName}</b>`,
  );
  const lastBudget = budgetStrings.pop();
  const allBudgets = `${budgetStrings.join(', ')}, and ${lastBudget}`;
  return `with a budget of ${allBudgets}`;
}

/** Format API date-time for display; invalid/empty → N/A. */
export function formatVacancyDateTime(dateString: string | null | undefined): string {
  if (!dateString || dateString.trim() === '') return 'N/A';
  try {
    return dayjs(dateString).format('YYYY.MM.DD HH:mm');
  } catch {
    return dateString;
  }
}

/** Sidebar date math (matches legacy VacancySidebar behavior). */
export function calculateVacancyDateStats(
  vacancyStartDate: string,
  vacancyDueDate: string,
  now: Date = new Date(),
): { noOfDaysRemaining: number; totalDays: number; daysPerPart: number } {
  const noOfDaysRemaining =
    Math.floor(
      (new Date(vacancyDueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    ) + 1;
  const totalDays =
    Math.floor(
      (new Date(vacancyDueDate).getTime() - new Date(vacancyStartDate).getTime()) /
        (1000 * 60 * 60 * 24),
    ) + 1;
  const daysPerPart = Math.floor(totalDays / 3);
  return { noOfDaysRemaining, totalDays, daysPerPart };
}

export type CountryApplicantStatus = {
  country: string | undefined;
  total: number;
  joined: number;
  rejected: number;
};

export function aggregateApplicantCounts(
  applicantCountryCounts: VacancyApiResponse['applicantCountryCounts'],
  locations: VacancyApiResponse['locations'],
): {
  totalApplicants: number;
  totalJoined: number;
  totalRejected: number;
  countryStatusCounts: CountryApplicantStatus[];
} {
  const totalApplicants = applicantCountryCounts.reduce(
    (acc, curr) => acc + curr.statusCount,
    0,
  );
  const totalJoined = applicantCountryCounts.reduce(
    (acc, curr) => (curr.currentStatus === 'Joined' ? acc + curr.statusCount : acc),
    0,
  );
  const totalRejected = applicantCountryCounts.reduce(
    (acc, curr) =>
      curr.currentStatus === 'Rejected' || curr.currentStatus === 'Offer Rejected'
        ? acc + curr.statusCount
        : acc,
    0,
  );

  const countryStatusCounts = locations.map(({ country }) => {
    const vacancyCountry = applicantCountryCounts.filter(
      ({ countryName }) => countryName === country?.name,
    );

    return {
      country: country?.name,
      total: vacancyCountry.reduce((acc, curr) => acc + curr.statusCount, 0),
      joined: vacancyCountry.reduce(
        (acc, curr) => (curr.currentStatus === 'Joined' ? acc + curr.statusCount : acc),
        0,
      ),
      rejected: vacancyCountry.reduce(
        (acc, curr) =>
          curr.currentStatus === 'Rejected' || curr.currentStatus === 'Offer Rejected'
            ? acc + curr.statusCount
            : acc,
        0,
      ),
    };
  });

  return {
    totalApplicants,
    totalJoined,
    totalRejected,
    countryStatusCounts,
  };
}

/** Total opportunities from customer rows (used with aggregateApplicantCounts). */
export function sumCustomerOpportunities(
  customers: VacancyApiResponse['customers'],
): number {
  return customers.reduce((sum, c) => sum + c.numberOfVacancies, 0) || 0;
}

/** Background gradients for View Vacancy sidebar header (matches VacancySidebar). */
export const SIDEBAR_GRADIENT_DEFAULT =
  'linear-gradient(180deg, #5B99DD 0%, #53C7EA 49.04%, #3A6DA5 100%)';
export const SIDEBAR_GRADIENT_FILLED =
  'linear-gradient(179.41deg, #6FAB72 -12.37%, #2BC838 35.69%, #0F9641 81.06%)';
export const SIDEBAR_GRADIENT_DEADLINE =
  'linear-gradient(357.21deg, #B1071D -3.29%, #FF7D80 51.3%, #FC406F 104.86%)';
export const SIDEBAR_GRADIENT_PROGRESS_MID =
  'linear-gradient(356.41deg, #4B4283 -2.73%, #6F58CD 50.97%, #A87FFF 99.74%)';
export const SIDEBAR_GRADIENT_PROGRESS_LATE =
  'linear-gradient(180.02deg, #F39045 -16.57%, #FFC45F 35.83%, #DA7145 87.23%)';

/**
 * Pure decision logic for sidebar header: which gradient and which content variant to show.
 * JSX rendering stays in VacancySidebar; tests assert on this function only.
 */
export type SidebarHeaderState =
  | { kind: 'filled_with_days'; bgGradient: string; daysRemaining: number }
  | { kind: 'filled_no_days'; bgGradient: string }
  | { kind: 'deadline_passed'; bgGradient: string; absDays: number }
  | { kind: 'progress_first'; bgGradient: string; headerDayText: string }
  | { kind: 'progress_second'; bgGradient: string; headerDayText: string }
  | { kind: 'progress_third'; bgGradient: string; headerDayText: string };

export function getSidebarHeaderState(
  totalOpportunities: number,
  totalApplicants: number,
  noOfDaysRemaining: number,
  daysPerPart: number,
): SidebarHeaderState {
  if (totalOpportunities === totalApplicants) {
    const bgGradient = SIDEBAR_GRADIENT_FILLED;
    if (noOfDaysRemaining > 0) {
      return { kind: 'filled_with_days', bgGradient, daysRemaining: noOfDaysRemaining };
    }
    return { kind: 'filled_no_days', bgGradient };
  }

  if (noOfDaysRemaining <= 0) {
    const absDays = Math.abs(noOfDaysRemaining);
    return {
      kind: 'deadline_passed',
      bgGradient: SIDEBAR_GRADIENT_DEADLINE,
      absDays,
    };
  }

  const headerDayText = `${noOfDaysRemaining} ${noOfDaysRemaining > 1 ? 'Days' : 'Day'}`;

  if (noOfDaysRemaining <= daysPerPart) {
    return {
      kind: 'progress_first',
      bgGradient: SIDEBAR_GRADIENT_DEFAULT,
      headerDayText,
    };
  }
  if (daysPerPart < noOfDaysRemaining && noOfDaysRemaining < 2 * daysPerPart) {
    return {
      kind: 'progress_second',
      bgGradient: SIDEBAR_GRADIENT_PROGRESS_MID,
      headerDayText,
    };
  }
  return {
    kind: 'progress_third',
    bgGradient: SIDEBAR_GRADIENT_PROGRESS_LATE,
    headerDayText,
  };
}
